import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
    Phone,
    Video,
    Calendar,
    MapPin,
    Star,
    Languages,
    ChevronDown,
    ChevronUp,
    Share2,
    Sparkles,
    Layers,
    Mail,
} from "lucide-react";
import ShareModal from "./sharemodal.tsx";
import { ICON_MAP } from "../utilities/iconcatalog.tsx";

// Canvas preview friendly, no external deps.
// Replace mock data with API props when wiring into Nettsiden/Teletorget backend.

function cn(...cls: Array<string | false | null | undefined>) {
    return cls.filter(Boolean).join(" ");
}

function VippsLogo({ className }: { className?: string }) {
    // Simple Vipps-style mark (circle + V). Replace with official asset if you have it in your design system.
    return (
        <svg viewBox="0 0 64 64" className={className} role="img" aria-label="Vipps">
            <circle cx="32" cy="32" r="30" fill="#ff5b24" />
            <path
                d="M18 22c0-2.2 1.8-4 4-4h3.2c1.7 0 3.2 1 3.8 2.6L32 38.8l3-8.2c.6-1.6 2.1-2.6 3.8-2.6H42c2.2 0 4 1.8 4 4v1.2c0 .5-.1 1-.3 1.5l-7.7 20.4c-.6 1.6-2.1 2.7-3.9 2.7h-4.2c-1.7 0-3.3-1.1-3.9-2.7l-7.7-20.4c-.2-.5-.3-1-.3-1.5V22z"
                fill="white"
            />
        </svg>
    );
}

type Availability = "available" | "busy" | "offline";

type Agent = {
    socials?: {
        linkedin?: string;
        x?: string;
        instagram?: string;
    };
    name: string;
    title: string;
    customer_minute_price: number;
    currency: string;
    unit: string;
    rating: number;
    reviews: number;
    responseTime: string;
    languages: string[];
    city: string;
    verified: boolean;
    code: string; // extension / routing number (e.g. 1115)
    availability: Availability;
    avatar: string;
    description: string;
    skills: string[];
    firstConsultFreeMins: number; // 0 if not offered
    call_status: Availability;
};

const MOCK_AGENT: Agent = {
    socials: {
        linkedin: "https://www.linkedin.com/in/johndoe/",
        x: "https://x.com/johndoe",
        instagram: "https://www.instagram.com/johndoe/",
    },
    name: "John Doe",
    title: "Partner og prosedyreadvokat, Forretnings- og strafferett",
    customer_minute_price: 40,
    currency: "kr",
    unit: "min",
    rating: 4.9,
    reviews: 128,
    responseTime: "Svarer vanligvis innen 30 min",
    languages: ["Norsk", "Engelsk"],
    city: "Oslo",
    verified: true,
    code: "1115",
    availability: "available", // available | busy | offline
    call_status: "available",
    avatar:
        "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=700&q=80",
    description: `John Doe er en profilert advokat og partner med over 20 års erfaring innen forretningsjus, strafferett og tvisteløsning. Han er kjent for sin skarpe juridiske analyse, strategiske argumentasjon og evne til å skape trygghet hos klientene – uansett sakens kompleksitet.

Gjennom karrieren har John representert både multinasjonale selskaper og privatpersoner i krevende saker for domstoler og voldgiftsretter, og har opparbeidet seg et sterkt rykte som en løsningsorientert forhandler med høy faglig integritet.

John er utdannet jurist fra Universitetet i Oslo, og har i tillegg spesialisering innen internasjonal strafferett fra London School of Economics.

Hans hovedområder omfatter:

- Næringslivstvister og kontraktsrett
- Økonomisk kriminalitet og etterforskning
- Internasjonal voldgift og mekling
- Forvaltningsrett og compliance

Han er medlem av både Den Norske Advokatforening og European Criminal Bar Association (ECBA), og har vært brukt som juridisk ekspert i flere nasjonale mediesaker.

Utenfor rettssalen er John kjent som en tydelig mentor for yngre jurister og en forkjemper for etikk, rettssikkerhet og profesjonell uavhengighet.

Hans motto: «Rettferdighet handler ikke bare om loven – men om motet til å følge den.»

Spesialfelt: kontraktsforhandlinger, tvister mellom aksjonærer, komplekse straffesaker, compliance og intern granskning, samt prosedyre i domstolene.`,
    skills: [
        "Petroleum",
        "Litigation",
        "Strafferett",
        "Forretningsjus",
        "Tvisteløsning",
        "Compliance",
        "Kontrakter",
        "Prosedyre",
        "Arbeidsrett",
    ],
    firstConsultFreeMins: 15,
};

function statusMeta(status: Availability) {
    if (status === "available") {
        return {
            label: "Ledig",
            long: "Ledig for samtale",
            dot: "bg-emerald-500",
            chip: "bg-emerald-50 text-emerald-700 ring-emerald-100",
            callout: "border-emerald-200 bg-emerald-50 text-emerald-900",
        };
    }
    if (status === "busy") {
        return {
            label: "Opptatt",
            long: "Opptatt",
            dot: "bg-red-500",
            chip: "bg-red-50 text-red-700 ring-red-100",
            callout: "border-red-200 bg-red-50 text-red-900",
        };
    }
    return {
        label: "Avlogget",
        long: "Avlogget",
        dot: "bg-slate-400",
        chip: "bg-slate-100 text-slate-700 ring-slate-200",
        callout: "border-slate-200 bg-slate-50 text-slate-900",
    };
}

const ACTIONS = [
    {
        key: "vipps",
        label: "Login",
        sub: "Vipps",
        icon: VippsLogo,
        variant: "vipps" as const,
    },
    {
        key: "free15",
        label: "Gratis",
        sub: "Konsultasjon",
        icon: Sparkles,
        variant: "highlight" as const,
    },
    {
        key: "call",
        label: "Ring",
        sub: "",
        icon: Phone,
        variant: "primary" as const,
    },
    // {
    //     key: "video",
    //     label: "Video",
    //     sub: "Trygg samtale",
    //     icon: Video,
    //     variant: "soft" as const,
    // },
    {
        key: "book",
        label: "Book",
        sub: "Velg tidspunkt",
        icon: Calendar,
        variant: "soft" as const,
    },
    {
        key: "email",
        label: "E-post",
        sub: "Send e-post",
        icon: Mail,
        variant: "ghost" as const,
    },
];

type Action = (typeof ACTIONS)[number];

type Variant = Action["variant"];

function StatPill({
    icon: Icon,
    children,
}: {
    icon: any;
    children: React.ReactNode;
}) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-sm text-slate-700 ring-1 ring-slate-200 backdrop-blur">
            <Icon className="h-4 w-4" />
            <span>{children}</span>
        </span>
    );
}

function StatusChip({ status }: { status: Availability }) {
    const m = statusMeta(status);
    return (
        <span
            className={cn(
                "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-sm font-semibold ring-1",
                m.chip
            )}
            title={m.long}
        >
            <span className={cn("inline-flex h-2.5 w-2.5 rounded-full", m.dot)} />
            {m.long}
        </span>
    );
}

function SocialIcons({
    socials,
    className,
}: {
    socials?: { linkedin?: string; x?: string; instagram?: string };
    className?: string;
}) {
    const hasAny = !!(socials?.linkedin || socials?.x || socials?.instagram);
    if (!hasAny) return null;

    const itemBase =
        "inline-flex h-8 w-8 items-center justify-center rounded-full transition";

    return (
        <div className={cn("flex items-center gap-2", className)}>
            {socials?.linkedin && (
                <a
                    href={socials.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LinkedIn"
                    className={cn(itemBase, "bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20")}
                    title="LinkedIn"
                >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.476-.9 1.637-1.85 3.37-1.85 3.602 0 4.266 2.37 4.266 5.455v6.286zM5.337 7.433a2.062 2.062 0 110-4.124 2.062 2.062 0 010 4.124zM6.814 20.452H3.861V9h2.953v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                </a>
            )}

            {socials?.x && (
                <a
                    href={socials.x}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="X"
                    className={cn(itemBase, "bg-black/10 text-black hover:bg-black/20")}
                    title="X"
                >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M18.244 2H21l-6.517 7.451L22.5 22h-6.29l-4.924-6.092L6.08 22H3.322l6.971-7.97L1.5 2h6.45l4.46 5.538L18.244 2z" />
                    </svg>
                </a>
            )}

            {socials?.instagram && (
                <a
                    href={socials.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    className={cn(itemBase, "bg-pink-500/10 text-pink-600 hover:bg-pink-500/20")}
                    title="Instagram"
                >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2zm0 1.5A4.25 4.25 0 003.5 7.75v8.5A4.25 4.25 0 007.75 20.5h8.5a4.25 4.25 0 004.25-4.25v-8.5A4.25 4.25 0 0016.25 3.5h-8.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 1.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm5.25-.88a1.13 1.13 0 110 2.26 1.13 1.13 0 010-2.26z" />
                    </svg>
                </a>
            )}
        </div>
    );
}

function FreeConsultChip({ mins }: { mins: number }) {
    if (!mins) return null;
    return (
        <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-50 to-sky-50 px-3 py-1 text-sm font-semibold text-slate-900 ring-1 ring-amber-200">
            <Sparkles className="h-4 w-4 text-amber-600" />
            Gratis {mins} min førstegangskonsultasjon
        </span>
    );
}

function ActionCard({
    action,
    onClick,
    status,
    show,
}: {
    action: Action;
    onClick: () => void;
    status: Availability;
    show: boolean;
}) {
    if (!show) return null;

    const Icon = action.icon;
    const base =
        "group relative w-full overflow-hidden rounded-2xl border p-4 text-left shadow-sm transition transform-gpu will-change-transform hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-300";

    const variants: Record<Variant, string> = {
        vipps:
            "border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 text-slate-900 hover:from-orange-100 hover:to-amber-100 hover:border-orange-300 hover:shadow-md",
        highlight:
            "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 text-slate-900 hover:from-amber-100 hover:via-amber-50 hover:to-sky-100 hover:shadow-md",
        // NOTE: primary base is overridden for CALL action based on availability (see below)
        primary:
            "border-sky-500/20 bg-gradient-to-br from-sky-600 to-indigo-700 text-white hover:from-sky-500 hover:to-indigo-600 hover:shadow-md",
        soft:
            "border-slate-200 bg-white hover:bg-sky-50 hover:border-sky-300 hover:shadow-md",
        ghost:
            "border-slate-200 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 hover:shadow-md",
    };
    const subVariants: Record<Variant, string> = {
        vipps: "text-slate-700",
        highlight: "text-slate-700",
        primary: "text-white/85",
        soft: "text-slate-600",
        ghost: "text-slate-600",
    };

    const statusM = statusMeta(status);

    // Status-aware styling for CALL CTA
    const callStatusClasses: Record<Availability, string> = {
        available:
            "border-emerald-400 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:from-emerald-400 hover:to-emerald-600",
        busy:
            "border-red-400 bg-gradient-to-br from-red-500 to-red-600 text-white hover:from-red-400 hover:to-red-600",
        offline:
            "border-slate-300 bg-gradient-to-br from-slate-400 to-slate-500 text-white hover:from-slate-400 hover:to-slate-500",
    };

    const isCall = action.key === "call";
    const variantClass = isCall
        ? callStatusClasses[status]
        : variants[action.variant];

    return (
        <button onClick={onClick} className={cn(base, variantClass)}>
            <div className="flex items-start gap-3">
                <div
                    className={cn(
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                        action.variant === "primary"
                            ? "bg-white/15"
                            : action.variant === "vipps"
                                ? "bg-orange-100/70"
                                : action.variant === "highlight"
                                    ? "bg-amber-100/60"
                                    : "bg-slate-100 group-hover:bg-slate-200"
                    )}
                >
                    {action.label === "Login" ? (
                        <img src="../../vipps.png" alt="vipps logo" />
                    ) : (
                        <Icon
                            className={cn(
                                "h-5 w-5",
                                action.variant === "primary"
                                    ? "text-white"
                                    : action.variant === "vipps"
                                        ? "text-orange-600"
                                        : action.variant === "highlight"
                                            ? "text-amber-700"
                                            : "text-slate-800"
                            )}
                        />
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <div className="text-base font-semibold leading-none">{action.label}</div>

                        {/* Status indikator på Ring nå */}
                        {action.key === "call" && (
                            <span className="ml-auto inline-flex items-center gap-2 rounded-full bg-white/10 px-2 py-0.5 text-xs font-semibold">
                                <span className={cn("inline-flex h-2 w-2 rounded-full", statusM.dot)} />
                                {statusM.label}
                            </span>
                        )}
                    </div>

                    <div className={cn("mt-1 line-clamp-1 text-sm", subVariants[action.variant])}>
                        {action.sub}
                    </div>
                </div>
            </div>

            {action.variant === "primary" && (
                <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/15 blur-2xl" />
            )}
        </button>
    );
}

function SectionCard({
    title,
    right,
    children,
}: {
    title: React.ReactNode;
    right?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">{title}</div>
                {right}
            </div>
            <div className="px-5 py-4">{children}</div>
        </div>
    );
}

type ReadMoreProps = {
    html: string;
};
function ReadMore({ html }: ReadMoreProps) {
    const [open, setOpen] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | "auto">("auto");

    const paragraphs = useMemo(() => {
        if (!html) return [];

        const clean = DOMPurify.sanitize(html, {
            USE_PROFILES: { html: true },
        });

        const doc = new DOMParser().parseFromString(clean, "text/html");

        return Array.from(doc.querySelectorAll("p"))
            .map((p) => p.innerHTML.trim())
            .filter(Boolean);
    }, [html]);

    const preview = paragraphs.slice(0, 2);
    const hasMore = paragraphs.length > 2;

    // Smooth height animation
    useEffect(() => {
        if (!contentRef.current) return;

        if (open) {
            setHeight(contentRef.current.scrollHeight);
        } else {
            setHeight(contentRef.current.scrollHeight);
            requestAnimationFrame(() => setHeight(0));
        }
    }, [open, paragraphs]);

    return (
        <div>
            {/* Collapsed preview */}
            {!open && (
                <div className="space-y-3 text-[15px] leading-7 text-slate-700">
                    {preview.map((p, i) => (
                        <div key={i} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                </div>
            )}

            {/* Expandable content */}
            <div
                ref={contentRef}
                style={{ height: open ? height : 0 }}
                className="overflow-hidden transition-[height] duration-500 ease-in-out"
            >
                {open && (
                    <div className="space-y-3 pt-3 text-[15px] leading-7 text-slate-700">
                        {paragraphs.map((p, i) => (
                            <div key={i} dangerouslySetInnerHTML={{ __html: p }} />
                        ))}
                    </div>
                )}
            </div>

            {hasMore && (
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                    {open ? (
                        <>
                            Vis mindre <ChevronUp className="h-4 w-4" />
                        </>
                    ) : (
                        <>
                            Les mer <ChevronDown className="h-4 w-4" />
                        </>
                    )}
                </button>
            )}
        </div>
    );
}

function StickyCTA({
    agent,
    onAction,
}: {
    agent: Agent;
    onAction: (key: string) => void;
}) {
    const m = statusMeta(agent.call_status);

    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-3 md:hidden">
            <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-lg backdrop-blur">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{agent.name}</div>
                        <div className="mt-0.5 flex items-center gap-2">
                            <span className="truncate text-xs text-slate-600">
                                Fra {agent.customer_minute_price} {agent.currency}/{"min"}
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
                                <span className={cn("h-2 w-2 rounded-full", m.dot)} />
                                {m.label}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onAction("call")}
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition transform-gpu hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-md"
                            title={m.long}
                        >
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className={cn(
                                        "inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-white/40",
                                        m.dot
                                    )}
                                />
                                <Phone className="h-4 w-4" />
                            </span>
                            Ring
                        </button>

                        <button
                            onClick={() => onAction("book")}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition transform-gpu hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow"
                        >
                            <Calendar className="h-4 w-4" />
                            Book
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

type AgentDetailProps = {
    agent: any,
    client: any,
    currentUser: any,
    baseUrl: string,
    handlePhoneClick: (uid: string, minute_price: number, call_startup_fee: string, agent_call_permission: string, type: string) => void,
    setShowBooking: (check: boolean) => void,
    onLoginClick: any,
}
export default function AgentDetail({ agent, client, currentUser, baseUrl, handlePhoneClick, setShowBooking, onLoginClick }: AgentDetailProps) {

    const description = JSON.parse(agent.description);

    const [toast, setToast] = useState<string | null>(null);
    const default_configs = client ? JSON.parse(client?.default_configs) : {};
    // TODO (prod): derive from your auth/session (Vipps login) instead of local state.
    const [isLoggedIn, setIsLoggedIn] = useState((currentUser?.role === "customer" ? true : (default_configs.vipps_pay === "no" ? true : false)));

    const skills = agent.skills.map(skill => skill.name);
    // dummy reviews and rating
    agent.rating = 4.9;
    agent.reviews = 155;
    agent.firstConsultFreeMins = 15;

    const topSkills = skills.slice(0, 5);
    const extraCount = Math.max(0, skills.length - topSkills.length);

    agent.socials = {
        linkedin: agent.linkedin_account,
        x: agent.twitter_account,
        instagram: agent.instagram_account,
    }

    const [openShare, setOpenShare] = useState(false);

    const onAction = (key: string) => {
        const msg =
            key === "vipps"
                ? onLoginClick()
                : key === "free15"
                    ? `Demo: Starter booking av gratis ${agent.firstConsultFreeMins} min (førstegangskonsultasjon)`
                    : key === "call"
                        ? agent.call_status === "available"
                            ? handlePhoneClick(agent.uid, agent.customer_minute_price, agent.customer_call_startup_fee, agent.receiving_calls_on, 'audio')
                            : agent.call_status === "busy"
                                ? "Demo: Agenten er opptatt (vis evt. kø / forslag om booking)"
                                : "Demo: Agenten er avlogget (vis booking i stedet)"
                        : key === "video"
                            ? handlePhoneClick(agent.uid, agent.customer_minute_price, agent.customer_call_startup_fee, agent.receiving_calls_on, 'video')
                            : key === "book"
                                ? setShowBooking(true)
                                : key === "email"
                                    ? window.location.href = "mailto:"+agent.email+"?subject=Henvendelse fra webområde"
                                    : key === "skills"
                                        ? "Demo: Åpner alle kompetanseområder"
                                        : key === "share"
                                            ? setOpenShare(true)
                                            : "Demo";

        setToast(msg);
        window.clearTimeout((onAction as any)._t || 0);
        (onAction as any)._t = window.setTimeout(() => setToast(null), 2600);
    };

    const s = statusMeta(agent.call_status);

    const onBack = () => {
        // Implementer logikk for å gå tilbake til forrige side eller agentliste
        window.history.back();
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-slate-50">
            {/* Local CSS helper for hiding horizontal scrollbars (scoped by class) */}
            <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>


            <button
                onClick={onBack}
                className="mb-6 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
                ← Tilbake
            </button>
            {/* TOP HEADER (no hero) */}
            <div className="mx-auto max-w-6xl px-4 pt-6 md:pt-8">
                <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                    {/* stronger color glow for more life */}
                    <div className="pointer-events-none absolute -top-12 left-8 hidden h-36 w-96 rounded-full bg-gradient-to-r from-sky-200/70 via-indigo-200/60 to-emerald-200/60 blur-3xl md:block" />
                    <div className="pointer-events-none absolute -bottom-14 right-10 hidden h-36 w-96 rounded-full bg-gradient-to-r from-amber-200/50 via-sky-200/40 to-indigo-200/40 blur-3xl md:block" />

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                        <div className="relative shrink-0">
                            <img
                                src={agent.image}
                                alt={agent.name}
                                className="h-32 w-32 rounded-3xl object-cover object-top ring-1 ring-slate-200 sm:h-36 sm:w-36"
                            />
                            {agent.verified && (
                                <div className="absolute -right-2 -top-2 rounded-2xl bg-white px-2 py-1 text-xs font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200">
                                    Verifisert
                                </div>
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                                    {agent.name}
                                </h1>

                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-sm text-slate-700 ring-1 ring-slate-200">
                                    <Star className="h-4 w-4" />
                                    {agent.rating} ({agent.reviews})
                                </span>

                                <StatusChip status={agent.call_status} />

                                {/* Innvalg badge */}
                                <span
                                    className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-2.5 py-1 text-sm font-semibold text-indigo-800 ring-1 ring-indigo-200"
                                    title={`Innvalg ${agent.code}`}
                                >
                                    <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500" />
                                    Innvalg {agent.code}
                                </span>
                                <button className="inline-flex items-center gap-2 justify-center rounded-full px-4 py-1 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => setOpenShare(true)}>
                                    <Share2 size={18} /> Del
                                </button>
                            </div>

                            <p className="mt-1 max-w-2xl text-sm text-slate-700 md:text-[15px]">
                                {agent.title}
                            </p>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <StatPill icon={MapPin}>{agent.city}</StatPill>
                                <StatPill icon={Languages}>{client.languages.split(",").join(" · ")}</StatPill>
                                {/* <StatPill icon={Clock}>{agent.responseTime}</StatPill> */}
                            </div>

                            {/* Free consult banner (high visibility) */}
                            {agent.firstConsultFreeMins > 0 && (
                                <div className="mt-3">
                                    <FreeConsultChip mins={agent.firstConsultFreeMins} />
                                </div>
                            )}

                            {/* Kompetanse: mobile horizontal scroll; desktop wrap */}
                            <div className="mt-3">
                                <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
                                    {topSkills.map((skill) => {
                                        const matchedSkill = agent.skills_list.find(
                                            (s) => s.name === skill
                                        );
                                        let HeaderIcon = null;
                                        if (matchedSkill) {
                                            const iconDef = matchedSkill.picture ? ICON_MAP[matchedSkill.picture] : undefined;
                                            HeaderIcon = iconDef?.Icon || Layers;
                                        }
                                        //skills_array array of objects contain skill and picture so match skills_array[0].name === skill and get skills_array[0].picture
                                        return (
                                            <span
                                                key={skill}
                                                className="shrink-0 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
                                            >
                                                {HeaderIcon && (
                                                    <HeaderIcon className="h-4 w-4 text-sky-600 mr-1" />
                                                )}
                                                {skill}
                                            </span>
                                        )
                                    })}
                                    {extraCount > 0 && (
                                        <button
                                            onClick={() => onAction("skills")}
                                            className="shrink-0 inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                                            title="Se alle kompetanseområder"
                                        >
                                            +{extraCount} flere
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Mobile: price + share */}
                            <div className="mt-4 md:hidden">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900">
                                        Fra {agent.customer_minute_price} {agent.currency}/{"min"}
                                    </span>
                                    <button
                                        onClick={() => onAction("share")}
                                        className="ml-auto inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm transition transform-gpu hover:-translate-y-0.5 hover:shadow-md hover:bg-slate-50 active:translate-y-0 active:shadow"
                                        title="Del"
                                    >
                                        <Share2 className="h-4 w-4" />
                                        Del
                                    </button>
                                </div>

                                {/* Mobile social icons (only if links exist) */}
                                <SocialIcons socials={agent.socials} className="mt-2" />
                            </div>

                            {/* Desktop hint row (more obvious status) */}
                            <div className="mt-4 hidden items-center gap-2 text-xs text-slate-700 md:flex">
                                <span
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1",
                                        s.callout
                                    )}
                                >
                                    <span className={cn("h-2 w-2 rounded-full", s.dot)} />
                                    {s.long}
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-sky-900 ring-1 ring-sky-100">
                                    Pris fra {agent.customer_minute_price} {agent.currency}/{"min"}
                                </span>
                                <SocialIcons socials={agent.socials} className="pl-2" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CONTENT */}
            <div className="mx-auto max-w-6xl px-4 pb-28 md:pb-10">
                <div className="grid grid-cols-1 gap-5">
                    <SectionCard title="Om agenten">
                        <ReadMore html={description?.norwegian} />
                    </SectionCard>

                    <SectionCard
                        title="Kontakt og booking"
                        right={
                            <div className="flex items-center gap-2">
                                {agent.firstConsultFreeMins > 0 && (
                                    <span className="hidden items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 sm:inline-flex">
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Gratis {agent.firstConsultFreeMins} min
                                    </span>
                                )}
                                <span className="text-sm font-semibold text-slate-900">
                                    Fra {agent.customer_minute_price} {agent.currency}/{"min"}
                                </span>
                            </div>
                        }
                    >
                        {/* Strong status callout inside CTA section */}
                        <div
                            className={cn(
                                "mb-3 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3",
                                s.callout
                            )}
                        >
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <span className={cn("h-2.5 w-2.5 rounded-full", s.dot)} />
                                {s.long}
                            </div>
                            {agent.call_status === "available" && (
                                <div className="text-xs font-medium opacity-90">Klar for samtale nå</div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                            {(() => {
                                // Ensure CALL card is always position #2
                                const visible = ACTIONS.filter((a) =>
                                    a.key === "vipps"
                                        ? !isLoggedIn
                                        : a.key === "free15"
                                            ? agent.firstConsultFreeMins > 0
                                            : a.key === "video"
                                                ? agent.can_video_call !== "0"
                                                : true
                                );

                                const call = visible.find((a) => a.key === "call");
                                const others = visible.filter((a) => a.key !== "call");

                                // Insert call as second item
                                const ordered = call
                                    ? [others[0], call, ...others.slice(1)].filter(Boolean)
                                    : others;

                                return ordered.map((a) => (
                                    <ActionCard
                                        key={a.key}
                                        action={a}
                                        status={agent.call_status}
                                        onClick={() => onAction(a.key)}
                                        show={true}
                                    />
                                ));
                            })()}
                        </div>
                    </SectionCard>
                </div>
            </div>

            {/* Mobile sticky CTA */}
            <StickyCTA agent={agent} onAction={onAction} />

            <ShareModal open={openShare} setOpen={setOpenShare} />
            {/* Toast */}
            {toast && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    className="fixed left-1/2 top-5 z-50 w-[92%] max-w-lg -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-3 text-sm text-slate-800 shadow-lg"
                >
                    {toast}
                </motion.div>
            )}
        </div>
    );
}
